/**
 * Melodic Dictation Drill — free-practice mode.
 *
 * Hear a melody in key context; reproduce it via solfège or piano input.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import { appendCadence, pickKey, MODE_PATTERNS, normalizeMode, type CadenceOptions, type KeyChoice } from "./utils.js";
import type { ScaleDegree, ScaleDegreeSequence } from "@music-app/theory-engine";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DictationMelodyLength = 3 | 4 | 5 | 6 | 7;
export type DictationRange = "narrow" | "wide";

export interface MelodicDictationExerciseConfig {
  length: DictationMelodyLength;
  range: DictationRange;
  chromatic: boolean;
  tonic: "fixed" | "random";
  fixedTonicMidi: number;
  drillMode: "graded" | "listening";
}

export interface MelodicDictationExerciseRound {
  expectedSequence: ScaleDegreeSequence;
  keyName: string;
  tonicMidi: number;
  length: DictationMelodyLength;
  range: DictationRange;
  chromaticEnabled: boolean;
  /**
   * Which of the node's representative items (1–5) this round credits. The
   * drill generates a fresh random melody each round, but each curriculum node
   * holds five interchangeable instance items — rotating across them lets free
   * drill advance the whole node instead of pinning all credit to `::1`.
   */
  instanceIndex: 1 | 2 | 3 | 4 | 5;
  audioPlan: AudioPlan;
  /** Slow-tempo replay for after wrong or listening-mode reveal. */
  teachAudio: AudioPlan;
}

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG: MelodicDictationExerciseConfig = {
  length: 4,
  range: "narrow",
  chromatic: false,
  tonic: "random",
  fixedTonicMidi: 60,
  drillMode: "graded",
};

// ─── Stats key ────────────────────────────────────────────────────────────────

/**
 * Format: `melodic-dictation-drill|${length}|${range}|${chromatic}|${tonic}`
 */
export function dictationDrillStatsKey(
  config: MelodicDictationExerciseConfig,
): string {
  const tonicPart = config.tonic === "random" ? "rng" : String(config.fixedTonicMidi);
  const chromStr  = config.chromatic ? "chromatic" : "diatonic";
  return `melodic-dictation-drill|${config.length}|${config.range}|${chromStr}|${tonicPart}`;
}

// ─── Curriculum item ID ───────────────────────────────────────────────────────

/**
 * Map a drill config to the closest curriculum item ID so FSRS credit and
 * Filter C signal accrue to the right node. The drill spans a wider parameter
 * space than the curriculum, so we bucket into three nodes — chromatic, medium
 * (length ≥ 4 or wide range), or narrow. Each node holds five interchangeable
 * instance items (::1–::5); `instanceIndex` (from the round) rotates across them
 * so free drill can advance the whole node, not just ::1.
 */
export function dictationExerciseItemId(
  config: MelodicDictationExerciseConfig,
  instanceIndex: 1 | 2 | 3 | 4 | 5 = 1,
): string {
  const node = config.chromatic
    ? "melodic-dictation-chromatic"
    : config.range === "wide" || config.length >= 4
      ? "melodic-dictation-medium"
      : "melodic-dictation-narrow";
  return `${node}::${instanceIndex}`;
}

// ─── Fixed-key helper ─────────────────────────────────────────────────────────

const SEMI_TO_LETTER: Record<number, { letter: string; accidental: number }> = {
  0:  { letter: "C", accidental: 0 },
  1:  { letter: "D", accidental: -1 },
  2:  { letter: "D", accidental: 0 },
  3:  { letter: "E", accidental: -1 },
  4:  { letter: "E", accidental: 0 },
  5:  { letter: "F", accidental: 0 },
  6:  { letter: "F", accidental: 1 },
  7:  { letter: "G", accidental: 0 },
  8:  { letter: "A", accidental: -1 },
  9:  { letter: "A", accidental: 0 },
  10: { letter: "B", accidental: -1 },
  11: { letter: "B", accidental: 0 },
};

function buildFixedKey(tonicMidi: number): KeyChoice {
  const semi = ((tonicMidi - 60) % 12 + 12) % 12;
  const { letter, accidental } = SEMI_TO_LETTER[semi]!;
  const accSym = accidental === -1 ? "♭" : accidental === 1 ? "♯" : "";
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi,
    name: `${letter}${accSym} major`,
  };
}

// ─── Melody generation ────────────────────────────────────────────────────────

const STEP_WEIGHTS: ReadonlyArray<{ step: number; weight: number }> = [
  { step: 1, weight: 5 },
  { step: 2, weight: 3 },
  { step: 3, weight: 2 },
  { step: 4, weight: 1 },
];

function sampleStep(rng: Rng, maxStep: number): number {
  const filtered = STEP_WEIGHTS.filter((s) => s.step <= maxStep);
  const total = filtered.reduce((acc, s) => acc + s.weight, 0);
  let r = rng.next() * total;
  for (const s of filtered) {
    r -= s.weight;
    if (r <= 0) return s.step;
  }
  return filtered[filtered.length - 1]!.step;
}

function wrapDegree(d: number): number {
  return ((d - 1) % 7 + 7) % 7 + 1;
}

function buildSequence(
  rng: Rng,
  length: number,
  range: DictationRange,
  chromatic: boolean,
): ScaleDegreeSequence {
  const startDegree = rng.pick([1, 3, 5]);
  const out: ScaleDegree[] = [{ degree: startDegree }];

  const maxStep = range === "narrow" ? 4 : 7;
  const envelope = range === "narrow" ? 4 : Infinity;
  let absPos = startDegree - 1;

  while (out.length < length) {
    const stepMag = sampleStep(rng, maxStep);
    const direction = rng.next() < 0.5 ? 1 : -1;
    const candidatePos = absPos + direction * stepMag;
    if (Math.abs(candidatePos - (startDegree - 1)) > envelope) {
      const flipped = absPos - direction * stepMag;
      if (Math.abs(flipped - (startDegree - 1)) > envelope) continue;
      absPos = flipped;
    } else {
      absPos = candidatePos;
    }

    const nextDegree = wrapDegree(absPos + 1);
    const sd: ScaleDegree = { degree: nextDegree };

    if (chromatic && (nextDegree === 4 || nextDegree === 6) && rng.next() < 0.25) {
      sd.alteration = nextDegree === 4 ? "♯" : "♭";
    }

    out.push(sd);
  }

  return out;
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

function sdToMidi(sd: ScaleDegree, key: KeyChoice): number {
  const pattern = MODE_PATTERNS[normalizeMode(key.mode)];
  const baseOffset = pattern[((sd.degree - 1) % 7 + 7) % 7]!;
  const altShift = sd.alteration === "♯" ? 1 : sd.alteration === "♭" ? -1 : 0;
  const octave = sd.octaveOffset ?? 0;
  return key.tonicMidi + 12 + baseOffset + altShift + octave * 12;
}

function buildTeachAudio(sequence: ScaleDegreeSequence, key: KeyChoice): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const sd of sequence) {
    builder.addNote(sdToMidi(sd, key), 1.2, 0.22, "melody");
    builder.advance(1.3);
  }
  builder.addNote(key.tonicMidi + 12, 1.2, 0.22, "target");
  builder.advance(1.5);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildMelodicDictationExerciseRound(
  config: MelodicDictationExerciseConfig,
  seed: number,
  cadenceOpts?: CadenceOptions,
): MelodicDictationExerciseRound {
  const rng = new Rng(seed);

  const key: KeyChoice =
    config.tonic === "fixed"
      ? buildFixedKey(config.fixedTonicMidi)
      : pickKey(rng, "major");

  // Derived from the seed so it doesn't perturb the melody rng draws below.
  const instanceIndex = ((Math.abs(Math.trunc(seed)) % 5) + 1) as 1 | 2 | 3 | 4 | 5;

  const sequence = buildSequence(rng, config.length, config.range, config.chromatic);

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  for (const sd of sequence) {
    builder.addNote(sdToMidi(sd, key), 0.6, 0.22, "target");
    builder.advance(0.65);
  }
  builder.advance(0.3);

  return {
    expectedSequence: sequence,
    keyName: key.name,
    tonicMidi: key.tonicMidi,
    length: config.length,
    range: config.range,
    chromaticEnabled: config.chromatic,
    instanceIndex,
    audioPlan: builder.build(),
    teachAudio: buildTeachAudio(sequence, key),
  };
}
